import React, { useEffect, useState } from "react";
import axios from "axios";
import "./DepartmentMasterModal.css";

const API = (
  process.env.REACT_APP_API_BASE_URL ||
  process.env.REACT_APP_API_URL ||
  (process.env.NODE_ENV === "development" ? "http://localhost:8080" : "")
).replace(/\/+$/, "");

const authHeaders = () => {
  const token = localStorage.getItem("token");
  return token ? { Authorization: `Bearer ${token}` } : {};
};

// Notes-only Department Master. This does NOT change ERP Roles.
export default function DepartmentMasterModal({ onClose, onChanged }) {
  const [departments, setDepartments] = useState([]);
  const [name, setName] = useState("");
  const [editingId, setEditingId] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(true);

  const reload = async () => {
    const response = await axios.get(`${API}/api/notes/departments`, {
      headers: authHeaders(),
    });
    if (!response.data?.canManageDepartments) {
      throw new Error("Only an ERP admin can manage departments.");
    }
    setDepartments(Array.isArray(response.data.departments) ? response.data.departments : []);
  };

  useEffect(() => {
    let active = true;
    axios.get(`${API}/api/notes/departments`, { headers: authHeaders() })
      .then(({ data }) => {
        if (!active) return;
        if (!data?.canManageDepartments) {
          setError("Only an ERP admin can manage departments.");
          return;
        }
        setDepartments(Array.isArray(data.departments) ? data.departments : []);
      })
      .catch((err) => {
        if (active) setError(err.response?.data?.message || "Unable to load departments.");
      })
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, []);

  const save = async (event) => {
    event.preventDefault();
    const cleanName = name.trim().replace(/\s+/g, " ");
    if (!cleanName) return setError("Enter a department name.");
    if (busy) return;
    setBusy(true);
    setError("");
    try {
      if (editingId) {
        await axios.patch(
          `${API}/api/notes/departments/${editingId}`,
          { name: cleanName },
          { headers: authHeaders() }
        );
      } else {
        await axios.post(
          `${API}/api/notes/departments`,
          { name: cleanName },
          { headers: authHeaders() }
        );
      }
      setName("");
      setEditingId("");
      await reload();
      await onChanged?.();
    } catch (err) {
      setError(err.response?.data?.message || err.message || "Unable to save department.");
    } finally {
      setBusy(false);
    }
  };

  const setActive = async (department) => {
    if (busy) return;
    const action = department.isActive ? "deactivate" : "activate";
    if (!window.confirm(`${action} ${department.name}? Existing notes will be preserved.`)) return;
    setBusy(true);
    setError("");
    try {
      await axios.patch(
        `${API}/api/notes/departments/${department._id}`,
        { isActive: !department.isActive },
        { headers: authHeaders() }
      );
      await reload();
      await onChanged?.();
    } catch (err) {
      setError(err.response?.data?.message || "Unable to change department status.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="ndm-overlay" onMouseDown={onClose}>
      <section
        className="ndm-panel"
        role="dialog"
        aria-modal="true"
        aria-label="Department Master"
        onMouseDown={(event) => event.stopPropagation()}
      >
        <header className="ndm-header">
          <div>
            <h2>Department Master</h2>
            <p>Notes-only departments — independent from ERP Roles.</p>
          </div>
          <button type="button" className="ndm-close" onClick={onClose} aria-label="Close">×</button>
        </header>

        <div className="ndm-body">
          {error && <div className="ndm-error" role="alert">{error}</div>}
          <form className="ndm-form" onSubmit={save}>
            <label htmlFor="ndm-name">Department name</label>
            <div className="ndm-form-row">
              <input
                id="ndm-name"
                value={name}
                onChange={(event) => setName(event.target.value)}
                placeholder="e.g. Design, Purchase, Site Execution"
                maxLength={80}
                required
              />
              <button type="submit" disabled={busy || !name.trim()}>
                {busy ? "Saving..." : editingId ? "Save Changes" : "+ Add Department"}
              </button>
              {editingId && (
                <button type="button" className="ndm-secondary" onClick={() => {
                  setEditingId("");
                  setName("");
                }}>Cancel</button>
              )}
            </div>
          </form>

          <div className="ndm-list-title">Departments ({departments.length})</div>
          {loading ? <p>Loading...</p> : departments.length === 0 ? (
            <p className="ndm-empty">No departments yet. Add your first department above.</p>
          ) : (
            <div className="ndm-list">
              {departments.map((department) => (
                <div className="ndm-item" key={department._id}>
                  <div className="ndm-info">
                    <strong>{department.name}</strong>
                    <span className={department.isActive ? "ndm-active" : "ndm-inactive"}>
                      {department.isActive ? "Active" : "Inactive"}
                    </span>
                  </div>
                  <div className="ndm-actions">
                    <button type="button" className="ndm-secondary" disabled={busy}
                      onClick={() => { setEditingId(department._id); setName(department.name); }}>
                      Edit
                    </button>
                    <button type="button" className="ndm-secondary" disabled={busy}
                      onClick={() => setActive(department)}>
                      {department.isActive ? "Deactivate" : "Activate"}
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </section>
    </div>
  );
}
